import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DownloadFileCloudIcon } from '@/assets/static/icons/download-file-cloud';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { useState } from 'react';
import styled from 'styled-components';

interface DownloadCustomExportTemplateProps {
  isOpen: boolean;
  funnelId: string;
  onClose: () => void;
}

const DownloadCustomExportTemplate = ({ isOpen, funnelId, onClose }: DownloadCustomExportTemplateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSave = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const result = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/due-diligence-funnel/${funnelId}/download-docx-template`, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        },
        responseType: 'blob'
      });

      // Extract filename from Content-Disposition header
      const contentDisposition = result.headers['content-disposition'];
      let filename = 'template.docx'; // default filename if for some reason the header is not present
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      fileDownload(result.data, filename);

      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!loading}
      showCancelButton={false}
      isUseOutsideClick={!loading}
      confirmButton={{ name: 'Download', onClick: onSave, disabled: loading }}
    >
      {errorMessage && (
        <ErrorBannerWrapper>
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        </ErrorBannerWrapper>
      )}
      <ModalContainer>
        <DownloadFileCloudIcon width={60} height={60} fill={themeVar('font.action')} />
        {loading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Download the Custom Export Template</ModalTitle>
          </>
        )}
      </ModalContainer>
    </ModalWrappedContent>
  );
};

export default DownloadCustomExportTemplate;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;

const ModalContainer = styled.div`
  text-align: center;
  font-weight: 400;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ModalTitle = styled.div`
  font-size: 33px;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;
